// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';
import {
  Badge,
  IconButton,
  Menu,
  Typography,
  Box,
  CircularProgress,
  Chip,
  useTheme,
  TextField,
  InputAdornment,
  List,
  ListItem,
  Tooltip,
} from '@mui/material';
import { BiMessageRoundedError, BiSearch, BiCheck, BiEnvelope } from 'react-icons/bi';
import { useUnreadMessages } from '@features/moves/hooks/useUnreadMessages';
import { markMessagesAsRead, markMoveAsUnread } from '@features/moves/services/smsReadStatusTracker';
import { formatMoveId } from '@services/moveService';
import { isMoveLite } from '@utils/tiers';
import { useValetSelfManaged } from '@hopdrive/feature-portal-products';
import { useNavigate } from 'react-router-dom';
import { movePath } from '@features/products/productRoutes';

// COMPONENT ---------------------------------------------------------------- //

const UnreadMessagesDropdown = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isSelfManaged = useValetSelfManaged();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [searchTerm, setSearchTerm] = React.useState('');
  const open = Boolean(anchorEl);

  const { unreadMoves, allMoves, totalUnreadCount, loading } = useUnreadMessages();

  const hasMessages = totalUnreadCount > 0;

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMoveClick = (moveId: number) => {
    navigate(movePath(moveId, { openChat: true }));
    handleClose();
    setSearchTerm('');
  };

  const handleMarkAsRead = async (e: React.MouseEvent, move: any) => {
    e.stopPropagation(); // Prevent navigation when clicking mark as read

    // Mark the messages as read for this move
    if (move.lastMessage?.id) {
      markMessagesAsRead(move.moveId, String(move.lastMessage.id));
    }
  };

  const handleMarkAsUnread = async (e: React.MouseEvent, move: any) => {
    e.stopPropagation(); // Prevent navigation when clicking mark as unread

    // Mark the conversation as unread using the last message ID
    if (move.lastMessage?.id) {
      markMoveAsUnread(move.moveId, String(move.lastMessage.id));
    }
  };

  // Filter moves based on search term
  const filteredMoves = React.useMemo(() => {
    if (!searchTerm) return allMoves;

    const lowerSearch = searchTerm.toLowerCase();
    return allMoves.filter(move => {
      const moveIdStr = formatMoveId(move.moveId, { isLite: isMoveLite(move.move) }).toLowerCase();
      const customerName = move.move?.consumer_name?.toLowerCase() || '';
      const vehicle =
        `${move.move?.vehicle_year || ''} ${move.move?.vehicle_make || ''} ${move.move?.vehicle_model || ''}`.toLowerCase();
      const referenceNum = move.move?.reference_num?.toLowerCase() || '';
      const vin = move.move?.vehicle_vin?.toLowerCase() || '';
      const stock = move.move?.vehicle_stock?.toLowerCase() || '';

      return (
        moveIdStr.includes(lowerSearch) ||
        customerName.includes(lowerSearch) ||
        vehicle.includes(lowerSearch) ||
        referenceNum.includes(lowerSearch) ||
        vin.includes(lowerSearch) ||
        stock.includes(lowerSearch)
      );
    });
  }, [searchTerm, allMoves]);

  // Don't render for a customer that is not self-managed-capable.
  if (!isSelfManaged) {
    return null;
  }

  return (
    <>
      <Badge
        badgeContent={loading ? '...' : (totalUnreadCount > 0 ? totalUnreadCount : undefined)}
        color={hasMessages ? 'error' : 'default'}
        overlap='circular'
        invisible={!loading && totalUnreadCount === 0}
        sx={{
          '& .MuiBadge-badge': {
            right: 1,
            top: 1,
            fontSize: '0.75rem',
            minWidth: '20px',
            height: '20px',
            padding: '0 6px',
            animation: hasMessages ? 'pulse 2s infinite' : 'none',
            '@keyframes pulse': {
              '0%': {
                boxShadow: '0 0 0 0 rgba(255, 0, 0, 0.7)',
              },
              '70%': {
                boxShadow: '0 0 0 10px rgba(255, 0, 0, 0)',
              },
              '100%': {
                boxShadow: '0 0 0 0 rgba(255, 0, 0, 0)',
              },
            },
          },
        }}
      >
        <Tooltip title={hasMessages ? `${totalUnreadCount} unread messages` : 'View all SMS conversations'}>
          <IconButton
            onClick={handleClick}
            sx={{
              width: 40,
              height: 40,
              color: hasMessages ? theme.palette.error.main : theme.palette.primary.main,
              backgroundColor: hasMessages ? theme.palette.error.light + '20' : theme.palette.primary.light + '10',
              '&:hover': {
                backgroundColor: hasMessages ? theme.palette.error.light + '30' : theme.palette.primary.light + '20',
              },
            }}
          >
            <BiMessageRoundedError size={24} />
          </IconButton>
        </Tooltip>
      </Badge>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          elevation: 6,
          sx: {
            mt: 1.5,
            width: 400,
            maxHeight: '70vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
          },
        }}
      >
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <TextField
            fullWidth
            size='small'
            placeholder='Search by move ID, customer, VIN, or stock...'
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <BiSearch size={18} />
                </InputAdornment>
              ),
            }}
          />
          {searchTerm && (
            <Typography variant='caption' color='text.secondary' sx={{ mt: 0.5, display: 'block' }}>
              {filteredMoves.length} of {allMoves.length} results
            </Typography>
          )}
        </Box>

        <List
          sx={{
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            maxHeight: 'calc(70vh - 120px)', // Account for header height
            py: 0,
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: theme.palette.grey[100],
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: theme.palette.grey[300],
              borderRadius: '4px',
              '&:hover': {
                backgroundColor: theme.palette.grey[400],
              },
            },
          }}
        >
          {loading && allMoves.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : filteredMoves.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4, px: 2 }}>
              <Typography variant='body2' color='text.secondary'>
                {searchTerm ? 'No results found' : 'No SMS conversations available'}
              </Typography>
            </Box>
          ) : (
            filteredMoves.map(move => (
              <ListItem
                key={move.moveId}
                onClick={() => handleMoveClick(move.moveId)}
                sx={{
                  cursor: 'pointer',
                  borderBottom: 1,
                  borderColor: 'divider',
                  px: 2,
                  py: 1.5,
                  opacity: move.hasUnread ? 1 : 0.6,
                  backgroundColor: move.hasUnread ? 'transparent' : theme.palette.grey[100],
                  borderLeft: move.hasUnread ? `4px solid ${theme.palette.primary.main}` : '4px solid transparent',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: move.hasUnread ? theme.palette.primary.light + '15' : theme.palette.action.hover,
                    opacity: 1,
                  },
                  '&:last-child': {
                    borderBottom: 0,
                  },
                }}
              >
                <Box sx={{ width: '100%' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Chip
                        label={formatMoveId(move.moveId, { isLite: isMoveLite(move.move) })}
                        size='small'
                        color={move.hasUnread ? 'primary' : 'default'}
                        variant={move.hasUnread ? 'filled' : 'outlined'}
                        sx={{ 
                          height: 20, 
                          fontSize: '0.7rem',
                          fontWeight: move.hasUnread ? 600 : 400
                        }}
                      />
                      <Typography variant='caption' color='text.secondary'>
                        {move.timeSinceLastMessage}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {!move.hasUnread && (
                        <Tooltip title='Mark as unread'>
                          <IconButton
                            size='small'
                            onClick={e => handleMarkAsUnread(e, move)}
                            sx={{
                              padding: '2px',
                              marginTop: '-4px',
                              color: theme.palette.text.secondary,
                              '&:hover': {
                                backgroundColor: theme.palette.action.hover,
                                color: theme.palette.primary.main,
                              },
                            }}
                          >
                            <BiEnvelope size={16} />
                          </IconButton>
                        </Tooltip>
                      )}
                      {move.hasUnread && (
                        <Tooltip title='Mark as read'>
                          <IconButton
                            size='small'
                            onClick={e => handleMarkAsRead(e, move)}
                            sx={{
                              padding: '2px',
                              marginTop: '-4px',
                              marginRight: '-8px',
                              color: theme.palette.text.secondary,
                              '&:hover': {
                                backgroundColor: theme.palette.action.hover,
                                color: theme.palette.success.main,
                              },
                            }}
                          >
                            <BiCheck size={18} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>

                  <Typography 
                    variant='body2' 
                    sx={{ 
                      fontWeight: move.hasUnread ? 600 : 400, 
                      mb: 0.25,
                      color: move.hasUnread ? theme.palette.text.primary : theme.palette.text.secondary
                    }}
                  >
                    {move.move?.consumer_name || 'Unknown Customer'}
                  </Typography>

                  {/* Vehicle info with VIN and Stock */}
                  <Box sx={{ mb: 0.5 }}>
                    <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>
                      {move.move?.vehicle_year} {move.move?.vehicle_make} {move.move?.vehicle_model}
                    </Typography>
                    {(move.move?.vehicle_vin || move.move?.vehicle_stock) && (
                      <Typography variant='caption' color='text.secondary' sx={{ display: 'block', opacity: 0.8 }}>
                        {move.move?.vehicle_vin && `VIN: ${move.move.vehicle_vin.slice(-6)}`}
                        {move.move?.vehicle_vin && move.move?.vehicle_stock && ' • '}
                        {move.move?.vehicle_stock && `Stock: ${move.move.vehicle_stock}`}
                      </Typography>
                    )}
                  </Box>

                  {/* Latest message preview */}
                  {move.lastMessage && (
                    <Box
                      sx={{
                        backgroundColor: move.hasUnread 
                          ? theme.palette.primary.light + '10' 
                          : theme.palette.grey[50],
                        borderRadius: 1,
                        p: 0.75,
                        border: move.hasUnread 
                          ? `1px solid ${theme.palette.primary.light}` 
                          : `1px solid ${theme.palette.grey[200]}`,
                      }}
                    >
                      <Typography
                        variant='caption'
                        sx={{
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontStyle: move.hasUnread ? 'normal' : 'italic',
                          fontWeight: move.hasUnread ? 500 : 400,
                          color: move.hasUnread 
                            ? theme.palette.text.primary 
                            : theme.palette.text.secondary,
                        }}
                      >
                        {move.lastMessage.message_body}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </ListItem>
            ))
          )}
        </List>
      </Menu>
    </>
  );
};

// EXPORT ---------------------------------------------------------------- //

export default UnreadMessagesDropdown;
